import type { Location } from 'history';
import type { ResolvedTransactionModel } from './intents';
import { explorerPath } from './serialize';

const PRESERVED_PARAMS = new Set(['hijackedUserId']);
const RESET_PARAMS = new Set(['page', 'sortBy', 'sortDir']);
const LEGACY_TRANSLATABLE = new Set(['date', 'accounts', 'imports', 'txnHash']);
const GROUPS_TRANSLATABLE = new Set(['date', 'accounts', 'imports', 'hash']);

type ExplorerLocation = { pathname: string; search: string };

export type ExplorerLocationTranslation =
  | { status: 'translated'; location: ExplorerLocation }
  | {
      status: 'unsupported';
      fallbackLocation: ExplorerLocation;
      unsupportedParams: string[];
    };

type ParsedFilterQuery =
  | { status: 'absent' }
  | { status: 'valid'; value: Record<string, unknown> }
  | { status: 'invalid' };

function parseFilterQuery(raw: string | null): ParsedFilterQuery {
  if (!raw) return { status: 'absent' };
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { status: 'invalid' };
    }
    return { status: 'valid', value: parsed as Record<string, unknown> };
  } catch {
    return { status: 'invalid' };
  }
}

function hasOnlyKeys(obj: Record<string, unknown>, allowed: Set<string>): boolean {
  return Object.keys(obj).every((key) => allowed.has(key) || obj[key] == null);
}

function mapHashFilters(
  filters: unknown,
  key: 'hash' | 'txnHash',
): Array<{ key: 'hash' | 'txnHash'; operator: unknown; value: unknown }> | undefined {
  if (!Array.isArray(filters)) return undefined;
  return filters.map((item) => {
    if (!item || typeof item !== 'object') {
      return { key, operator: 'Is', value: item };
    }
    const filter = item as { operator?: unknown; value?: unknown };
    return { key, operator: filter.operator ?? 'Is', value: filter.value };
  });
}

function translateFilterQuery(
  parsedFilterQuery: ParsedFilterQuery,
  target: ResolvedTransactionModel,
):
  | { status: 'translated'; filterQuery: Record<string, unknown> | null }
  | { status: 'unsupported'; unsupportedParams: string[] } {
  if (parsedFilterQuery.status === 'absent') {
    return { status: 'translated', filterQuery: null };
  }
  if (parsedFilterQuery.status === 'invalid') {
    return { status: 'unsupported', unsupportedParams: ['filterQuery'] };
  }

  const filterQuery = parsedFilterQuery.value;
  const sourceKeys = target === 'transferGroups' ? LEGACY_TRANSLATABLE : GROUPS_TRANSLATABLE;
  if (!hasOnlyKeys(filterQuery, sourceKeys)) {
    return {
      status: 'unsupported',
      unsupportedParams: Object.keys(filterQuery).filter(
        (key) => !sourceKeys.has(key) && filterQuery[key] != null,
      ),
    };
  }

  if (target === 'transferGroups') {
    const { txnHash, ...rest } = filterQuery;
    return {
      status: 'translated',
      filterQuery: {
        ...rest,
        ...(txnHash ? { hash: mapHashFilters(txnHash, 'hash') } : {}),
      },
    };
  }

  const { hash, ...rest } = filterQuery;
  return {
    status: 'translated',
    filterQuery: {
      ...rest,
      ...(hash ? { txnHash: mapHashFilters(hash, 'txnHash') } : {}),
    },
  };
}

function buildExplorerLocation(
  params: URLSearchParams,
  target: ResolvedTransactionModel,
  filterQuery: Record<string, unknown> | null,
): ExplorerLocation {
  const next = new URLSearchParams();

  PRESERVED_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) next.set(key, value);
  });

  if (filterQuery && Object.keys(filterQuery).length > 0) {
    next.set('filterQuery', JSON.stringify(filterQuery));
  }

  if (target === 'transferGroups') {
    const transferGroupId = params.get('transferGroupId');
    if (transferGroupId) next.set('transferGroupId', transferGroupId);
  }

  const search = next.toString();
  return {
    pathname: explorerPath(target),
    search: search ? `?${search}` : '',
  };
}

function unsupportedQueryParams(params: URLSearchParams, target: ResolvedTransactionModel): string[] {
  const supportedParams = new Set([...PRESERVED_PARAMS, ...RESET_PARAMS, 'filterQuery']);
  if (target === 'transferGroups') supportedParams.add('transferGroupId');
  return [...params.keys()].filter((key) => !supportedParams.has(key));
}

export function translateExplorerLocation(
  location: Pick<Location, 'search'>,
  target: ResolvedTransactionModel,
): ExplorerLocationTranslation {
  const params = new URLSearchParams(location.search);
  const filterTranslation = translateFilterQuery(parseFilterQuery(params.get('filterQuery')), target);
  const unsupportedParams = unsupportedQueryParams(params, target);

  if (filterTranslation.status === 'unsupported' || unsupportedParams.length > 0) {
    return {
      status: 'unsupported',
      fallbackLocation: buildExplorerLocation(params, target, null),
      unsupportedParams: [
        ...new Set([
          ...unsupportedParams,
          ...(filterTranslation.status === 'unsupported' ? filterTranslation.unsupportedParams : []),
        ]),
      ],
    };
  }

  return {
    status: 'translated',
    location: buildExplorerLocation(params, target, filterTranslation.filterQuery),
  };
}
